import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, ArrowRight, Heart, Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import Seo from "@/components/Seo";
import { postJSON } from "@/lib/api";
import { ASSUNTOS_POR_ETAPA, OUTROS, expandAssuntos } from "@/data/assuntos";

const ETAPAS = ["Planejamento", "Gestação", "Parto", "Pós-parto"];
const TIPOS_PROFISSIONAL = [
  "Obstetra", "Pediatra", "Doula", "Nutricionista", "Psicólogo(a) perinatal",
  "Consultora de amamentação", "Fisioterapeuta pélvico(a)", "Ainda não sei",
];

const STORAGE_KEY = "mat_familias_form";
const EMPTY = {
  nome: "", email: "", whatsapp: "", cidade: "", bairro: "",
  etapa: "", assuntos: [], outrosTexto: "", tipoProfissional: "", mensagem: "", consentimento: false,
};

const inputClass =
  "w-full bg-white border border-maternnia-nevoa rounded-xl px-4 py-3 text-sm text-maternnia-charcoal placeholder:text-maternnia-taupe/70 focus:outline-none focus:ring-2 focus:ring-maternnia-pink/30 focus:border-maternnia-pink transition";

const FamiliasPage = () => {
  const [form, setForm] = useState(() => {
    try { const s = localStorage.getItem(STORAGE_KEY); return s ? { ...EMPTY, ...JSON.parse(s) } : EMPTY; }
    catch { return EMPTY; }
  });
  const [errors, setErrors] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [sucesso, setSucesso] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(form)); } catch { /* ignore */ }
  }, [form]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  // ao trocar de etapa, os assuntos mudam: mantém só os válidos para a nova etapa
  const setEtapa = (etapa) =>
    setForm((f) => {
      const validos = [...(ASSUNTOS_POR_ETAPA[etapa] || []), OUTROS];
      return { ...f, etapa, assuntos: f.assuntos.filter((a) => validos.includes(a)) };
    });
  const toggleAssunto = (a) =>
    setForm((f) => ({
      ...f,
      assuntos: f.assuntos.includes(a) ? f.assuntos.filter((x) => x !== a) : [...f.assuntos, a],
    }));

  const validate = () => {
    const e = {};
    if (!form.nome.trim()) e.nome = "Informe seu nome.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Informe um e-mail válido.";
    if (!form.cidade.trim()) e.cidade = "Informe sua cidade.";
    if (!form.etapa) e.etapa = "Selecione a sua etapa.";
    if (!form.consentimento) e.consentimento = "É necessário aceitar para continuar.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setEnviando(true);
    try {
      const assuntosPayload = expandAssuntos(form.assuntos, form.outrosTexto);
      await postJSON("/leads", {
        nome: form.nome,
        email: form.email,
        whatsapp: form.whatsapp,
        cidade: form.cidade,
        bairro: form.bairro,
        etapa: form.etapa,
        assuntos: assuntosPayload,
        tipoProfissional: form.tipoProfissional,
        mensagem: form.mensagem,
      });
      setSucesso(true);
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
      setForm(EMPTY);
    } catch (e) {
      toast.error("Não foi possível enviar", { description: e.message });
    } finally {
      setEnviando(false);
    }
  };

  if (sucesso) {
    return (
      <main data-testid="familias-page" className="bg-maternnia-porcelana/50 min-h-screen flex items-center">
        <div className="max-w-xl mx-auto px-6 py-24 text-center">
          <motion.div
            data-testid="familias-sucesso"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="bg-white rounded-[2rem] p-10 md:p-14 shadow-[0_24px_64px_rgba(43,37,38,0.10)]"
          >
            <div className="h-16 w-16 mx-auto rounded-2xl bg-maternnia-eucalipto/10 flex items-center justify-center mb-6">
              <CheckCircle2 size={30} className="text-maternnia-eucalipto" />
            </div>
            <h1 className="font-serif text-3xl md:text-4xl text-maternnia-charcoal font-medium">Recebemos o seu cadastro!</h1>
            <p className="mt-4 text-maternnia-taupe font-light leading-relaxed">
              Em breve você começará a receber materiais e conteúdos selecionados para a sua etapa. Que bom ter você com a gente. 💗
            </p>
            <Link to="/" className="mt-8 inline-flex items-center gap-2 bg-maternnia-pink text-white hover:bg-maternnia-pinkHover rounded-full px-7 py-3.5 text-sm font-medium transition-all duration-300 hover:scale-[1.03]">
              Voltar ao início <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="familias-page" className="bg-maternnia-porcelana/50 min-h-screen">
      <Seo
        title="Cadastro para famílias"
        path="/familias"
        description="Cadastre-se gratuitamente e receba conteúdos selecionados para a sua etapa da maternidade."
      />
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-2 bg-maternnia-pink/10 text-maternnia-pink text-xs font-medium px-4 py-2 rounded-full mb-5">
            <Heart size={14} /> Para famílias • Gratuito
          </span>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-maternnia-charcoal font-medium">
            Receba conteúdos para o seu momento.
          </h1>
          <p className="mt-5 text-base md:text-lg text-maternnia-taupe leading-relaxed font-light max-w-xl mx-auto">
            Conte em que etapa você está e receba materiais, conteúdos e novidades relacionados à sua jornada.
          </p>
        </div>

        <form onSubmit={handleSubmit} data-testid="familias-form" noValidate className="bg-white rounded-[2rem] p-7 md:p-10 shadow-[0_24px_64px_rgba(43,37,38,0.08)] space-y-6">
          {Object.keys(errors).length > 0 && (
            <div data-testid="familias-erro" role="alert" aria-live="assertive" className="flex items-center gap-2.5 bg-red-50 text-red-600 rounded-xl px-4 py-3 text-sm">
              <AlertCircle size={16} /> Por favor, revise os campos destacados.
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label htmlFor="fam-nome" className="block text-sm font-medium text-maternnia-charcoal mb-1.5">Nome *</label>
              <input id="fam-nome" data-testid="fam-nome" type="text" value={form.nome} onChange={(e) => set("nome", e.target.value)} placeholder="Seu nome" className={inputClass} aria-required="true" aria-invalid={!!errors.nome} aria-describedby={errors.nome ? "fam-erro-nome" : undefined} />
              {errors.nome && <p id="fam-erro-nome" data-testid="fam-erro-nome" className="mt-1 text-xs text-red-600">{errors.nome}</p>}
            </div>
            <div>
              <label htmlFor="fam-email" className="block text-sm font-medium text-maternnia-charcoal mb-1.5">E-mail *</label>
              <input id="fam-email" data-testid="fam-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="seu@email.com" className={inputClass} aria-required="true" aria-invalid={!!errors.email} aria-describedby={errors.email ? "fam-erro-email" : undefined} />
              {errors.email && <p id="fam-erro-email" data-testid="fam-erro-email" className="mt-1 text-xs text-red-600">{errors.email}</p>}
            </div>
            <div>
              <label htmlFor="fam-whatsapp" className="block text-sm font-medium text-maternnia-charcoal mb-1.5">WhatsApp <span className="text-maternnia-taupe/70 font-light">(opcional)</span></label>
              <input id="fam-whatsapp" data-testid="fam-whatsapp" type="tel" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="(11) 9 0000-0000" className={inputClass} />
            </div>
            <div>
              <label htmlFor="fam-etapa" className="block text-sm font-medium text-maternnia-charcoal mb-1.5">Etapa atual *</label>
              <select id="fam-etapa" data-testid="fam-etapa" value={form.etapa} onChange={(e) => setEtapa(e.target.value)} className={`${inputClass} ${form.etapa === "" ? "text-maternnia-taupe/70" : ""}`} aria-required="true" aria-invalid={!!errors.etapa} aria-describedby={errors.etapa ? "fam-erro-etapa" : undefined}>
                <option value="" disabled>Selecione</option>
                {ETAPAS.map((x) => <option key={x}>{x}</option>)}
              </select>
              {errors.etapa && <p id="fam-erro-etapa" data-testid="fam-erro-etapa" className="mt-1 text-xs text-red-600">{errors.etapa}</p>}
            </div>
            <div>
              <label htmlFor="fam-cidade" className="block text-sm font-medium text-maternnia-charcoal mb-1.5">Cidade *</label>
              <input id="fam-cidade" data-testid="fam-cidade" type="text" value={form.cidade} onChange={(e) => set("cidade", e.target.value)} placeholder="Sua cidade" className={inputClass} aria-required="true" aria-invalid={!!errors.cidade} aria-describedby={errors.cidade ? "fam-erro-cidade" : undefined} />
              {errors.cidade && <p id="fam-erro-cidade" data-testid="fam-erro-cidade" className="mt-1 text-xs text-red-600">{errors.cidade}</p>}
            </div>
            <div>
              <label htmlFor="fam-bairro" className="block text-sm font-medium text-maternnia-charcoal mb-1.5">Bairro <span className="text-maternnia-taupe/70 font-light">(opcional)</span></label>
              <input id="fam-bairro" data-testid="fam-bairro" type="text" value={form.bairro} onChange={(e) => set("bairro", e.target.value)} placeholder="Seu bairro" className={inputClass} />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-maternnia-charcoal mb-3">Assuntos de interesse</label>
            {!form.etapa ? (
              <p data-testid="fam-assuntos-hint" className="text-sm text-maternnia-taupe/80 font-light bg-maternnia-porcelana/60 rounded-xl px-4 py-3">
                Selecione a sua etapa acima para ver os assuntos relacionados ao seu momento.
              </p>
            ) : (
              <>
                <div className="flex flex-wrap gap-2.5">
                  {[...(ASSUNTOS_POR_ETAPA[form.etapa] || []), OUTROS].map((a) => {
                    const ativo = form.assuntos.includes(a);
                    return (
                      <button
                        key={a} type="button" data-testid={`fam-assunto-${a}`} onClick={() => toggleAssunto(a)}
                        className={`text-sm font-medium rounded-full px-4 py-2 transition-all duration-300 ${ativo ? "bg-maternnia-pink text-white" : "bg-maternnia-porcelana text-maternnia-taupe hover:bg-maternnia-nevoa"}`}
                      >
                        {ativo && <Check size={13} className="inline mr-1 -mt-0.5" />}{a}
                      </button>
                    );
                  })}
                </div>
                {form.assuntos.includes(OUTROS) && (
                  <input
                    data-testid="fam-outros-texto" type="text" value={form.outrosTexto}
                    onChange={(e) => set("outrosTexto", e.target.value)}
                    placeholder="Conte qual outro assunto você gostaria de receber"
                    className={`${inputClass} mt-3`}
                  />
                )}
              </>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-maternnia-charcoal mb-1.5">Tipo de profissional procurado</label>
            <select data-testid="fam-tipo-profissional" value={form.tipoProfissional} onChange={(e) => set("tipoProfissional", e.target.value)} className={`${inputClass} ${form.tipoProfissional === "" ? "text-maternnia-taupe/70" : ""}`}>
              <option value="">Selecione (opcional)</option>
              {TIPOS_PROFISSIONAL.map((x) => <option key={x}>{x}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-maternnia-charcoal mb-1.5">Como podemos ajudar? <span className="text-maternnia-taupe/70 font-light">(opcional)</span></label>
            <textarea data-testid="fam-mensagem" rows={3} value={form.mensagem} onChange={(e) => set("mensagem", e.target.value)} placeholder="Conte um pouco sobre o que você procura..." className={`${inputClass} resize-none`} />
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input data-testid="fam-consentimento" type="checkbox" checked={form.consentimento} onChange={(e) => set("consentimento", e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-maternnia-nevoa accent-maternnia-pink shrink-0" />
            <span className="text-xs text-maternnia-taupe leading-relaxed font-light">
              Concordo em receber conteúdos e novidades da Maternnia e li a{" "}
              <a href="/privacidade" target="_blank" rel="noreferrer" className="text-maternnia-pink underline underline-offset-2 hover:text-maternnia-pinkHover">política de privacidade</a>. Meus dados não serão compartilhados sem consentimento.
            </span>
          </label>
          {errors.consentimento && <p data-testid="fam-erro-consentimento" className="-mt-3 text-xs text-red-500">{errors.consentimento}</p>}

          <button
            type="submit" data-testid="fam-submit" disabled={enviando}
            className="group w-full inline-flex items-center justify-center gap-2 bg-maternnia-pink text-white hover:bg-maternnia-pinkHover rounded-full px-8 py-4 text-base font-medium transition-all duration-300 hover:shadow-lg hover:scale-[1.01] active:scale-[0.99] disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {enviando ? (<><Loader2 size={18} className="animate-spin" /> Enviando...</>) : (<>Quero receber gratuitamente <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" /></>)}
          </button>

          <p className="text-center text-xs text-maternnia-taupe/80 font-light">
            Não pedimos diagnósticos, exames, sintomas ou histórico médico. Apenas o essencial para te enviar conteúdos relevantes.
          </p>
        </form>
      </div>
    </main>
  );
};

export default FamiliasPage;
